import { X } from 'lucide-react';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { cn } from '../components/ui.js';
import type { RouterOutputs } from '../lib/trpc.js';
import { listOf } from './owner-dashboard.js';

/**
 * The click-through tour of a read-only view link, for a package whose `viewLinks` has a
 * `tour`: the package's welcome, then a few stops on the first view, each lighting up one part
 * of the page with a sentence on what it is for, written from the dashboard's own words (their
 * question, the first thing that needs them, the names of their views), and the package's
 * invite last. It opens by itself on the first visit and from "Take the tour" after that. A
 * part a dashboard doesn't have, such as an owner's number, is a stop the tour leaves out.
 */

type View = RouterOutputs['viewer']['view'];

/** Where this browser remembers that the tour was seen. */
const SEEN_KEY = 'databink.viewer.tour';

/** Seen on this page, for a browser whose storage takes nothing. */
let seenHere = false;

/** Whether this browser has seen the tour. Storage that can't be read counts as seen, so a
 * browser that blocks it isn't shown the tour on every visit. */
export function tourSeen(): boolean {
  if (seenHere) return true;
  try {
    return window.localStorage.getItem(SEEN_KEY) !== null;
  } catch {
    return true;
  }
}

export function markTourSeen(): void {
  seenHere = true;
  try {
    window.localStorage.setItem(SEEN_KEY, new Date().toISOString());
  } catch {
    // Not remembered: the tour opens again on the next visit.
  }
}

interface Stop {
  /** The `data-tour` mark of the part it lights up; null for the opening card. */
  target: string | null;
  title: string;
  body: ReactNode;
}

type Invite = { title: string; text: string | null; links: { label: string }[] };

/** The stops this dashboard has, in order. */
function stopsFor(
  company: string,
  welcome: string,
  view: View,
  views: { label: string }[],
  invite: Invite | null,
): Stop[] {
  const sources = view.sources.map((s) => s.label);
  const asked = view.owner_number?.asked;
  const first = view.attention[0];
  const book = invite?.links[0]?.label;
  return [
    {
      target: null,
      title: `Your dashboard, ${company}`,
      body: (
        <>
          {welcome}
          {view.sample &&
            (sources.length > 0
              ? ` The numbers are samples; yours would come from ${listOf(sources)}.`
              : ' The numbers are samples.')}{' '}
          Here’s what each part is for.
        </>
      ),
    },
    {
      target: 'number',
      title: 'Your question, answered first',
      body: asked ? (
        <>
          You told us you wish you knew <q className="italic">{asked.replace(/[.?!]+$/, '')}</q>.
          This is the answer, at the top every morning.
        </>
      ) : (
        'The number that matters most to you, at the top every morning.'
      ),
    },
    {
      target: 'figures',
      title: 'Your numbers at a glance',
      body: 'The handful of numbers you’d check first thing, brought up to date before your day starts.',
    },
    {
      target: 'needs',
      title: 'What needs you today',
      body: first ? (
        <>
          What’s worth your attention, most urgent first. Right now that starts with{' '}
          <strong className="font-semibold">{first.label.toLowerCase()}</strong>. Click any line to
          see what’s behind it.
        </>
      ) : (
        'What’s worth your attention, most urgent first. Click any line to see what’s behind it.'
      ),
    },
    {
      target: 'views',
      title: 'Built around your business',
      body:
        views.length > 1
          ? `${views.length} views made for ${company}: ${listOf(views.map((v) => v.label))}. Each one covers a different part of your day.`
          : `A view made for ${company}, covering the part of your day you asked about.`,
    },
    {
      target: 'questions',
      title: 'Questions, answered',
      body: 'The questions you’d ask your office, answered from the same numbers. Click one to see its answer.',
    },
    ...(invite && book
      ? [
          {
            target: 'book',
            title: invite.title,
            body: [invite.text, `Use “${book}” to get started.`].filter(Boolean).join(' '),
          },
        ]
      : []),
  ];
}

/** The part of the page a stop lights up: the first one with its mark that is on screen. */
function findTarget(mark: string): HTMLElement | null {
  const marked = document.querySelectorAll<HTMLElement>(`[data-tour="${mark}"]`);
  for (const el of marked) if (el.getClientRects().length > 0) return el;
  return null;
}

type Box = { top: number; left: number; width: number; height: number };

/** Space around the lit-up part, and the gap to the card beside it. */
const PAD = 8;
const GAP = 12;
const CARD_WIDTH = 360;

export function Tour({
  company,
  welcome,
  view,
  views,
  invite,
  onClose,
}: {
  company: string;
  welcome: string;
  view: View;
  views: { label: string }[];
  invite: Invite | null;
  onClose: () => void;
}) {
  // The stops are the parts on the page, so they are worked out once it shows the view.
  const [stops, setStops] = useState<Stop[]>([]);
  useLayoutEffect(() => {
    setStops(
      stopsFor(company, welcome, view, views, invite).filter(
        (s) => s.target === null || findTarget(s.target),
      ),
    );
    // Worked out once, when the tour opens.
  }, []);
  const [at, setAt] = useState(0);
  const [box, setBox] = useState<Box | null>(null);
  const card = useRef<HTMLDivElement>(null);
  const primary = useRef<HTMLButtonElement>(null);
  const stop = stops[at];
  const last = at === stops.length - 1;

  const measure = useCallback(() => {
    const el = stop?.target ? findTarget(stop.target) : null;
    if (!el) return setBox(null);
    const r = el.getBoundingClientRect();
    setBox({
      top: r.top - PAD,
      left: r.left - PAD,
      width: r.width + PAD * 2,
      height: r.height + PAD * 2,
    });
  }, [stop]);

  // Each stop brings its part into view, then the light follows it as the page moves.
  useLayoutEffect(() => {
    const el = stop?.target ? findTarget(stop.target) : null;
    el?.scrollIntoView({ block: 'center', behavior: 'instant' });
    measure();
    primary.current?.focus();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [stop, measure]);

  useEffect(() => {
    const key = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight' && !last) setAt((i) => i + 1);
      if (e.key === 'ArrowLeft' && at > 0) setAt((i) => i - 1);
      // Tab stays in the card, as the page behind it can't be used while the tour is open.
      if (e.key === 'Tab' && card.current) {
        const inCard = [...card.current.querySelectorAll<HTMLElement>('button')];
        const i = inCard.indexOf(document.activeElement as HTMLElement);
        const next = (i + (e.shiftKey ? -1 : 1) + inCard.length) % inCard.length;
        e.preventDefault();
        inCard[i === -1 ? 0 : next]?.focus();
      }
    };
    window.addEventListener('keydown', key);
    return () => window.removeEventListener('keydown', key);
  }, [at, last, onClose]);

  if (!stop) return null;
  const narrow = window.innerWidth < 640;
  // Beside the lit-up part: under it when there is room, else over it; on a phone, a sheet at
  // the bottom of the screen; the opening card in the middle.
  const place = (): CSSProperties => {
    if (narrow) return { left: 12, right: 12, bottom: 12 };
    if (!box)
      return { left: '50%', top: '50%', transform: 'translate(-50%, -50%)', width: CARD_WIDTH };
    const left = Math.min(Math.max(12, box.left), window.innerWidth - CARD_WIDTH - 12);
    const below = box.top + box.height + GAP;
    const height = card.current?.offsetHeight ?? 200;
    const top = below + height < window.innerHeight - 12 ? below : box.top - GAP - height;
    // Kept on screen, so its buttons can always be reached, wherever the page has scrolled to.
    const lowest = Math.max(12, window.innerHeight - height - 12);
    return { left, top: Math.min(Math.max(12, top), lowest), width: CARD_WIDTH };
  };

  return (
    <div data-testid="tour">
      {/* Clicks outside the card do nothing while the tour is open. */}
      <div className={cn('fixed inset-0 z-40', !box && 'bg-shade')} aria-hidden />
      {box && (
        <div
          aria-hidden
          className="pointer-events-none fixed z-40 rounded-2xl ring-2 ring-brand transition-all duration-200"
          style={{ ...box, boxShadow: '0 0 0 9999px var(--color-shade)' }}
        />
      )}
      <div
        ref={card}
        role="dialog"
        aria-modal="true"
        aria-labelledby="tour-title"
        aria-describedby="tour-body"
        className="fixed z-50 max-h-[calc(100dvh-24px)] overflow-y-auto rounded-2xl bg-surface p-5 shadow-xl dark:ring-1 dark:ring-line-2"
        style={place()}
      >
        <div className="flex items-start justify-between gap-3">
          <p className="text-xs font-semibold tracking-wide text-brand-strong uppercase">
            {at === 0 ? 'Quick tour' : `${at} of ${stops.length - 1}`}
          </p>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close the tour"
            className="-m-1 rounded-md p-1 text-ink-3 hover:bg-wash hover:text-ink"
          >
            <X className="size-4" aria-hidden />
          </button>
        </div>
        <h2 id="tour-title" className="mt-1 text-lg font-bold tracking-tight text-ink">
          {stop.title}
        </h2>
        <p id="tour-body" className="mt-1.5 text-sm leading-relaxed text-ink-2">
          {stop.body}
        </p>
        <div className="mt-4 flex items-center justify-between gap-3">
          {at === 0 ? (
            <button
              type="button"
              onClick={onClose}
              className="text-sm font-medium text-ink-3 hover:text-ink"
            >
              Not now
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setAt(at - 1)}
              className="text-sm font-medium text-ink-3 hover:text-ink"
            >
              Back
            </button>
          )}
          <button
            ref={primary}
            type="button"
            onClick={() => (last ? onClose() : setAt(at + 1))}
            className="rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-deep"
          >
            {at === 0 ? 'Start the tour' : last ? 'Done' : 'Next'}
          </button>
        </div>
      </div>
    </div>
  );
}
